import {
  AppBar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Slide,
  Tab,
  Tabs,
  Toolbar,
  Typography
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { Alert, AlertTitle } from '@mui/material';
import { HubDto, DeviceDto } from '@groundfloor-platform/mgmt-service-g-sdk';
import { forwardRef, useEffect, useMemo, useState } from 'react';
import { useQueryGetProject } from '../../../../../core/queries/projects.queries';
import { useHasUIFeatures } from '../../../../../core/permissions/has-permissions.hook';
import { parseErrorMessage } from '../../../../../core/api/api.context';
import { useQueryFindDevicesByHub, useQueryGetDeviceDetails } from '../../../device-management.queries';
import { DeviceViewer } from './device-viewer.component';
import { extractTrmmAgentMesh, TrmmViewKind } from './trmm-agent-mesh.types';
import { useIframeInactivityTimeout } from './inactivity-timeout.hook';

const INACTIVITY_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes
const INACTIVITY_PROMPT_MS = 30 * 1000; // 30 seconds

const Transition: any = forwardRef(function Transition(props: any, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const useStyles = makeStyles((theme) => ({
  paper: {
    minHeight: 900,
    minWidth: 600,
    width: '70vw',
    height: '70vh',
    maxWidth: 'none',
    maxHeight: 'none',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    transition: theme.transitions.create(['width', 'height'], {
      duration: theme.transitions.duration.shorter
    })
  },
  paperMaximized: {
    width: '90vw',
    height: 'calc(100vh - 140px)',
    marginTop: 100,
    marginBottom: 40
  },
  scrollPaperMaximized: {
    alignItems: 'flex-start'
  },
  appBar: {
    position: 'relative',
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    boxShadow: 'none',
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  title: {
    flex: 1,
    marginLeft: theme.spacing(1)
  },
  body: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    backgroundColor: theme.palette.background.default
  },
  centered: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(3)
  },
  iframeContainer: {
    flex: 1,
    minHeight: 0,
    display: 'flex'
  },
  devicePicker: {
    width: '100%',
    maxWidth: 480
  }
}));

export function RemoteViewerModal({ hub, projectID, onClose }: { hub: HubDto; projectID: string; onClose(): void }) {
  const classes = useStyles();

  const projectQuery = useQueryGetProject(projectID);
  const devicesQuery = useQueryFindDevicesByHub(hub.id);
  const { hasAllFeatures: canUseExperimentalViews } = useHasUIFeatures(['experimental']);

  const [selectedDeviceID, setSelectedDeviceID] = useState<string | null>(null);
  const [view, setView] = useState<TrmmViewKind>('control');
  const [isMaximized, setIsMaximized] = useState(false);
  const [iframeEl, setIframeEl] = useState<HTMLIFrameElement | null>(null);
  const [showInactivityPrompt, setShowInactivityPrompt] = useState(false);

  const { reset: resetInactivity, stop: stopInactivity } = useIframeInactivityTimeout({
    iframeEl,
    timeoutMs: INACTIVITY_TIMEOUT_MS,
    onInactive: () => {
      setShowInactivityPrompt(true);
    }
  });

  // Auto-select when exactly one device is returned.
  useEffect(() => {
    if (selectedDeviceID) return;
    const devices = devicesQuery.data;
    if (devices && devices.length === 1) {
      setSelectedDeviceID(devices[0].id);
    }
  }, [devicesQuery.data, selectedDeviceID]);

  const deviceDetailsQuery = useQueryGetDeviceDetails(selectedDeviceID ?? undefined);
  const mesh = useMemo(() => extractTrmmAgentMesh(deviceDetailsQuery.data), [deviceDetailsQuery.data]);

  const title = useMemo(() => {
    const projectName = projectQuery.data?.name;
    return projectName ? `${projectName} — ${hub.name}` : hub.name;
  }, [projectQuery.data?.name, hub.name]);

  return (
    <Dialog
      open
      onClose={onClose}
      classes={{
        paper: `${classes.paper} ${isMaximized ? classes.paperMaximized : ''}`,
        scrollPaper: isMaximized ? classes.scrollPaperMaximized : undefined
      }}
      slots={{
        transition: Transition
      }}
    >
      <AppBar className={classes.appBar}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" className={classes.title}>
            {title}
          </Typography>
          {mesh && canUseExperimentalViews && (
            <Tabs
              value={view}
              onChange={(_, v) => {
                setView(v);
                resetInactivity();
              }}
              indicatorColor="primary"
              textColor="primary"
            >
              <Tab label="Remote Control" value="control" />
              <Tab label="Terminal" value="terminal" />
              <Tab label="File" value="file" />
            </Tabs>
          )}
          <IconButton
            color="inherit"
            onClick={() => setIsMaximized((m) => !m)}
            aria-label={isMaximized ? 'restore' : 'maximize'}
          >
            {isMaximized ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box className={classes.body}>
        <RemoteViewerBody
          classes={classes}
          hubID={hub.id}
          devicesQuery={devicesQuery}
          deviceDetailsQuery={deviceDetailsQuery}
          selectedDeviceID={selectedDeviceID}
          onSelectDevice={setSelectedDeviceID}
          onCancel={onClose}
          mesh={mesh}
          view={view}
          iframeRef={setIframeEl}
        />
      </Box>
      {showInactivityPrompt && (
        <InactivityPrompt
          countdownMs={INACTIVITY_PROMPT_MS}
          onStay={() => {
            setShowInactivityPrompt(false);
            resetInactivity();
          }}
          onTimeout={() => {
            stopInactivity();
            onClose();
          }}
        />
      )}
    </Dialog>
  );
}

function InactivityPrompt({
  countdownMs,
  onStay,
  onTimeout
}: {
  countdownMs: number;
  onStay(): void;
  onTimeout(): void;
}) {
  const [remainingMs, setRemainingMs] = useState(countdownMs);

  useEffect(() => {
    const startedAt = Date.now();
    const interval = setInterval(() => {
      const left = Math.max(0, countdownMs - (Date.now() - startedAt));
      setRemainingMs(left);
      if (left === 0) {
        clearInterval(interval);
        onTimeout();
      }
    }, 250);
    return () => clearInterval(interval);
  }, [countdownMs, onTimeout]);

  const seconds = Math.ceil(remainingMs / 1000);

  return (
    <Dialog open onClose={onStay} maxWidth="xs" fullWidth>
      <DialogTitle>Are you still here?</DialogTitle>
      <DialogContent>
        <Typography>
          The remote viewer will close in {seconds} second{seconds === 1 ? '' : 's'} due to inactivity.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onTimeout}>Close now</Button>
        <Button onClick={onStay} color="primary" variant="contained">
          I'm still here
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function RemoteViewerBody({
  classes,
  hubID,
  devicesQuery,
  deviceDetailsQuery,
  selectedDeviceID,
  onSelectDevice,
  onCancel,
  mesh,
  view,
  iframeRef
}: {
  classes: ReturnType<typeof useStyles>;
  hubID: string;
  devicesQuery: ReturnType<typeof useQueryFindDevicesByHub>;
  deviceDetailsQuery: ReturnType<typeof useQueryGetDeviceDetails>;
  selectedDeviceID: string | null;
  onSelectDevice(id: string): void;
  onCancel(): void;
  mesh: ReturnType<typeof extractTrmmAgentMesh>;
  view: TrmmViewKind;
  iframeRef(el: HTMLIFrameElement | null): void;
}) {
  if (devicesQuery.isLoading) {
    return (
      <Box className={classes.centered}>
        <CircularProgress />
        <Typography variant="body2" color="textSecondary">
          Looking for devices…
        </Typography>
      </Box>
    );
  }

  const devicesError = devicesQuery.error;
  const isNoDevicesError = devicesQuery.isError && devicesError?.statusCode === 404;

  if (isNoDevicesError || (!devicesQuery.isError && (devicesQuery.data ?? []).length === 0)) {
    return (
      <Box className={classes.centered}>
        <Alert variant="outlined" severity="info">
          <AlertTitle>No devices found for this hub</AlertTitle>
          There are no remote devices registered for hub {hubID} that can use this feature. The device may still be
          accessible via TeamViewer or Splashtop.
        </Alert>
        <Button variant="outlined" onClick={onCancel}>
          Close
        </Button>
      </Box>
    );
  }

  if (devicesQuery.isError) {
    return (
      <Box className={classes.centered}>
        <Alert variant="outlined" severity="error">
          <AlertTitle>Something went wrong</AlertTitle>
          {parseErrorMessage(devicesError?.message) || 'Failed to find devices for this hub.'}
        </Alert>
        <Button variant="outlined" onClick={onCancel}>
          Close
        </Button>
      </Box>
    );
  }

  const devices = devicesQuery.data ?? [];

  if (!selectedDeviceID) {
    return (
      <Box className={classes.centered}>
        <Typography variant="subtitle1">Select a device</Typography>
        <List className={classes.devicePicker}>
          {devices.map((device) => (
            <DevicePickerItem key={device.id} device={device} onSelect={() => onSelectDevice(device.id)} />
          ))}
        </List>
        <Button variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
      </Box>
    );
  }

  if (deviceDetailsQuery.isLoading) {
    return (
      <Box className={classes.centered}>
        <CircularProgress />
        <Typography variant="body2" color="textSecondary">
          Loading device…
        </Typography>
      </Box>
    );
  }

  if (deviceDetailsQuery.isError) {
    return (
      <Box className={classes.centered}>
        <Alert variant="outlined" severity="error">
          <AlertTitle>Something went wrong</AlertTitle>
          {parseErrorMessage(deviceDetailsQuery.error?.message) || 'Failed to load device details.'}
        </Alert>
        <Button variant="outlined" onClick={onCancel}>
          Close
        </Button>
      </Box>
    );
  }

  if (!mesh) {
    return (
      <Box className={classes.centered}>
        <Alert variant="outlined" severity="warning">
          <AlertTitle>Remote viewer unavailable</AlertTitle>
          This device does not have a TRMM agent configured or cannot be found. The device may still be accessible via
          TeamViewer or Splashtop.
        </Alert>
        <Button variant="outlined" onClick={onCancel}>
          Close
        </Button>
      </Box>
    );
  }

  return (
    <Box className={classes.iframeContainer}>
      <DeviceViewer mesh={mesh} view={view} iframeRef={iframeRef} />
    </Box>
  );
}

function DevicePickerItem({ device, onSelect }: { device: DeviceDto; onSelect(): void }) {
  return (
    <ListItemButton onClick={onSelect} divider>
      <ListItemText
        primary={device.hostname || device.id}
        secondary={[device.siteName, device.description, device.status].filter(Boolean).join(' · ')}
      />
    </ListItemButton>
  );
}
